import { i18n } from '../i18n';
import type { ActionType, ActionQuality } from '../store/useGameStore';

type ActionWithQuality =
  | 'Service'
  | 'Réception'
  | 'Passe'
  | 'Attaque'
  | 'Bloc'
  | 'Défense'
  | 'Freeball Donnée'
  | 'Freeball Reçue'
  | 'Retour Direct';

type ActionWithoutQuality = Exclude<ActionType, ActionWithQuality>;

const ACTION_TO_I18N_KEY: Record<ActionWithQuality, string> = {
  'Service':         'service',
  'Réception':       'reception',
  'Passe':           'passe',
  'Attaque':         'attaque',
  'Bloc':            'bloc',
  'Défense':         'defense',
  'Freeball Donnée': 'freeball_donnee',
  'Freeball Reçue':  'freeball_recue',
  'Retour Direct':   'retour_direct',
};

const QUALITY_TO_I18N_KEY: Record<ActionQuality, string> = {
  '#': 'hash',
  '+': 'plus',
  '!': 'excl',
  '-': 'minus',
  '/': 'slash',
  '=': 'equal',
  '0': 'zero',
};

export const ACTIONS_WITH_QUALITY: ActionWithQuality[] = [
  'Service', 'Réception', 'Passe', 'Attaque', 'Bloc',
  'Défense', 'Freeball Donnée', 'Freeball Reçue', 'Retour Direct',
];

export const ACTIONS_WITHOUT_QUALITY: ActionWithoutQuality[] = [
  'Faute Adversaire',
  'Point Adversaire',
  'Faute de Fil',
  'Mordu',
  'Double Touches',
  'Porté',
  'Quatre Touches',
  'Faute de Position',
  'Substitution',
  'Temps Mort',
  'Temps Mort Adversaire',
];

export const QUALITY_CODE_ORDER: ActionQuality[] = ['#', '+', '!', '-', '/', '=', '0'];

export const QUALITY_COLORS: Record<ActionQuality, string> = {
  '#': '#059669',
  '+': '#34d399',
  '!': '#facc15',
  '-': '#fb923c',
  '/': '#f87171',
  '=': '#dc2626',
  '0': '#94a3b8',
};

export const getActionLabel = (action: string, quality: ActionQuality): string => {
  const actionKey = ACTION_TO_I18N_KEY[action as ActionWithQuality];
  const qualityKey = QUALITY_TO_I18N_KEY[quality];

  if (actionKey && qualityKey) {
    const key = `game.action_quality.${actionKey}.${qualityKey}`;
    const translated = i18n.t(key);
    if (translated !== key) return translated;
  }

  if (qualityKey) {
    return i18n.t(`game.quality_generic.${qualityKey}`);
  }

  return quality;
};

export const getGenericQualityLabel = (quality: ActionQuality): string => {
  const qualityKey = QUALITY_TO_I18N_KEY[quality];
  return qualityKey ? i18n.t(`game.quality_generic.${qualityKey}`) : quality;
};

export const getActionName = (action: string): string => {
  const key = `game.actions.${action}`;
  const translated = i18n.t(key);
  return translated !== key ? translated : action;
};

export const getActionQualityI18nKey = (action: string, quality: ActionQuality): string => {
  const actionKey = ACTION_TO_I18N_KEY[action as ActionWithQuality];
  const qualityKey = QUALITY_TO_I18N_KEY[quality];
  if (actionKey && qualityKey) {
    return `game.action_quality.${actionKey}.${qualityKey}`;
  }
  return qualityKey ? `game.quality_generic.${qualityKey}` : '';
};

export const hasQuality = (action: string): action is ActionWithQuality => {
  return action in ACTION_TO_I18N_KEY;
};

export const getQualityI18nKey = (quality: ActionQuality): string => {
  return QUALITY_TO_I18N_KEY[quality] ?? '';
};

export const getActionI18nKey = (action: string): string => {
  return ACTION_TO_I18N_KEY[action as ActionWithQuality] ?? '';
};
